import { ClientConfigObject } from "app/config/interfaces";
import fetch from "isomorphic-unfetch";
import * as Sentry from "@sentry/browser";

import { Performance } from "./interfaces";

const checkHttpStatusCode = (response: any): any => {
    if (response.status >= 200 && response.status < 300) {
        return response;
    } else {
        const error = new Error(response.statusText) as any;
        error.response = response;
        throw error;
    }
};

/**
 * Request a JSON file from a given url, check if the status code is fine
 * and return the JSON as object.
 */
export function getJsonFromUrl<T = any>(url: string): Promise<T> {
    return fetch(url)
        .then(checkHttpStatusCode)
        .then(res => res.json())
        .catch(e => {
            Sentry.captureException(e);
            throw e;
        });
}

export interface FrontendStore {
    url: string | null;
    config: ClientConfigObject;
    cache: {
        [cacheObj: string]: any;
    };
    instruments: any;

    [key: string]: any;
}

/**
 * Takes an instrument object and wraps it into a store structure that can be forwarded
 * to a data provider context.
 */
export const createStore = (
    instrument: { isin: string; [x: string]: any } | null,
    url: string | undefined,
    config: ClientConfigObject
): FrontendStore => {
    let store = {
        url: url || null,
        config,
        cache: {},
        instruments: {}
    };

    if (instrument) {
        // @ts-ignore
        store.instruments[instrument.isin] = instrument;
    }

    return store;
};

/**
 * Takes an instrument object and wraps it into a store structure that can be forwarded
 * to a data provider context.
 */
export const createAnalysisDetailStore = (
    analysis: { id: string; [x: string]: any },
    url: string | undefined,
    config: ClientConfigObject
) => ({
    url,
    config,
    analysis: {
        [analysis.id]: analysis
    }
});

/**
 * Takes an instrument object and wraps it into a store structure that can be forwarded
 * to a data provider context.
 */
export const createNewsDetailStore = (
    news: { id: string; [x: string]: any },
    url: string | undefined,
    config: ClientConfigObject
) => ({
    url,
    config,
    news: {
        [news.id]: news
    }
});

/**
 * Takes a path string (/level1/level2/) and returns the path stripped by the passed maxLevel
 */
export const stripPath = (path: string, maxLevel: number) =>
    `/${path
        .replace(/^\/+/, "")
        .split("/")
        .slice(0, maxLevel)
        .join("/")}`;

/**
 * Returns true or false, depending on whether the media query is matching or not
 */
export const isMobile = () =>
    typeof window !== "undefined" && window.matchMedia("screen and (max-width: 768px)").matches;

/**
 * Adjusts the performance object of an instrument to allow changing middleware schemas, and to
 * support varying requirements in components
 */
export const preparePerformance = (performance: Performance) => {
    if (performance) {
        performance.periods = performance.periods || performance.periodValues;
        performance.years = performance.years || performance.yearValues;

        if (performance.byPeriods) {
            performance.periods = [];
            performance.periods.push({ label: "1 Jahr", value: performance.byPeriods.OneYear });
            performance.periods.push({ label: "2 Jahre", value: performance.byPeriods.TwoYears });
            performance.periods.push({ label: "3 Jahre", value: performance.byPeriods.ThreeYears });
        }

        if (performance.byYears) {
            performance.years = [];

            const currentYear = new Date().getFullYear();
            const validYears = [currentYear, currentYear - 1, currentYear - 2];

            performance.years = validYears.map(year => {
                if (performance.byYears && year in performance.byYears) {
                    return {
                        label: year.toString(10) as string,
                        value: performance.byYears[year]
                    } as any;
                }
                return {
                    label: year.toString(10) as string,
                    value: null as null
                } as any;
            });
        }
    }
};

/**
 * Gets the value at path of object. If the resolved value is undefined, the defaultValue is returned in its place.
 *
 * @param {object} obj The object to query.
 * @param {array|string} path The path of the property to get.
 * @param {any} defaultValue The value returned for undefined resolved values.
 * @returns {*} Returns the resolved value.
 */
export const get = (obj: object, path: any, defaultValue: any) => {
    const travel = (regexp: any) =>
        String.prototype.split
            .call(path, regexp)
            .filter(Boolean)
            .reduce((res: any, key: string) => (res !== null && res !== undefined ? res[key] : res), obj);
    const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
    return result === undefined || result === obj ? defaultValue : result;
};
